import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Check, EyeOff, Trash2, UserX, RefreshCw, ChevronDown, ExternalLink, User, Loader2, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getCsrf = () => {
  const m = document.cookie.split('; ').find(r => r.startsWith('tcl_csrf='));
  return m ? m.split('=')[1] : '';
};

const STATUS_TABS = [
  { id: 'pending', label: 'Pending', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  { id: 'approved', label: 'Approved', color: 'text-teal-400', bg: 'bg-teal-500/10 border-teal-500/20' },
  { id: 'hidden', label: 'Hidden', color: 'text-cream/30', bg: 'bg-cream/[0.04] border-cream/[0.06]' },
];

/* ── Confirm Block Modal ── */
const ConfirmBlockModal = ({ comment, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
    <div className="bg-navy border border-gold/[0.08] rounded-xl max-w-sm w-full p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/15 flex items-center justify-center flex-shrink-0">
          <UserX size={14} className="text-red-400" />
        </div>
        <div>
          <p className="text-cream/80 text-sm font-medium">Block user?</p>
          <p className="text-cream/30 text-xs mt-0.5">
            This will hide all comments from <strong className="text-cream/50">{comment.reader_name}</strong> and prevent future submissions.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-2 text-xs text-cream/40 hover:text-cream/70 border border-cream/[0.06] rounded-lg transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm} data-testid="confirm-block-btn" className="flex-1 py-2 text-xs bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/15 rounded-lg transition-colors">
          Block user
        </button>
      </div>
    </div>
  </div>
);

/* ── Comment Card ── */
const CommentCard = ({ comment, onApprove, onHide, onDelete, onBlock, actionLoading }) => {
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const isLoading = actionLoading === comment.id;

  return (
    <div className="bg-navy border border-gold/[0.05] rounded-xl p-4 hover:border-gold/[0.1] transition-colors" data-testid={`comment-card-${comment.id}`}>
      <div className="flex items-start justify-between gap-3">
        {/* Meta */}
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-8 h-8 rounded-full bg-gold/[0.1] border border-gold/[0.08] flex items-center justify-center flex-shrink-0">
            <span className="text-gold/60 text-xs font-medium">{(comment.reader_name || 'R')[0].toUpperCase()}</span>
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="text-cream/70 text-xs font-medium">{comment.reader_name}</span>
              <span className="text-cream/20 text-[10px]">·</span>
              <span className="text-cream/20 text-[10px]">
                {new Date(comment.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
              {comment.article_slug && (
                <>
                  <span className="text-cream/20 text-[10px]">·</span>
                  <a
                    href={`/articles/${comment.article_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold/40 text-[10px] hover:text-gold/70 flex items-center gap-0.5 transition-colors"
                    data-testid={`comment-article-link-${comment.id}`}
                  >
                    {comment.article_title || comment.article_slug}
                    <ExternalLink size={8} />
                  </a>
                </>
              )}
            </div>
            <p className="text-cream/55 text-sm leading-relaxed">{comment.content}</p>
          </div>
        </div>

        {/* Actions */}
        {isLoading ? (
          <Loader2 size={14} className="animate-spin text-cream/20 flex-shrink-0 mt-1" />
        ) : (
          <div className="flex items-center gap-1 flex-shrink-0">
            {comment.status !== 'approved' && (
              <button onClick={() => onApprove(comment.id)} data-testid={`approve-btn-${comment.id}`} title="Approve" className="w-7 h-7 flex items-center justify-center rounded-lg text-teal-400/50 hover:text-teal-400 hover:bg-teal-500/[0.08] transition-colors">
                <Check size={13} />
              </button>
            )}
            {comment.status !== 'hidden' && (
              <button onClick={() => onHide(comment.id)} data-testid={`hide-btn-${comment.id}`} title="Hide" className="w-7 h-7 flex items-center justify-center rounded-lg text-cream/20 hover:text-cream/50 hover:bg-cream/[0.05] transition-colors">
                <EyeOff size={13} />
              </button>
            )}
            {comment.status === 'hidden' && (
              <button onClick={() => onApprove(comment.id)} data-testid={`unhide-btn-${comment.id}`} title="Approve (unhide)" className="w-7 h-7 flex items-center justify-center rounded-lg text-teal-400/50 hover:text-teal-400 hover:bg-teal-500/[0.08] transition-colors">
                <Check size={13} />
              </button>
            )}
            <button onClick={() => onDelete(comment.id)} data-testid={`delete-btn-${comment.id}`} title="Delete permanently" className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400/30 hover:text-red-400 hover:bg-red-500/[0.08] transition-colors">
              <Trash2 size={13} />
            </button>
            <button onClick={() => setShowBlockConfirm(true)} data-testid={`block-btn-${comment.id}`} title="Block user" className="w-7 h-7 flex items-center justify-center rounded-lg text-cream/15 hover:text-red-400 hover:bg-red-500/[0.05] transition-colors">
              <UserX size={13} />
            </button>
          </div>
        )}
      </div>

      {showBlockConfirm && (
        <ConfirmBlockModal
          comment={comment}
          onConfirm={() => { setShowBlockConfirm(false); onBlock(comment.reader_id, comment.reader_name); }}
          onCancel={() => setShowBlockConfirm(false)}
        />
      )}
    </div>
  );
};

/* ── Blocked Users Panel ── */
const BlockedUsersPanel = ({ blockedUsers, onUnblock, unblockLoading }) => {
  const [open, setOpen] = useState(false);
  if (blockedUsers.length === 0) return null;
  return (
    <div className="mt-6 bg-navy border border-red-500/[0.08] rounded-xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-cream/[0.01] transition-colors" data-testid="blocked-users-toggle">
        <div className="flex items-center gap-3">
          <UserX size={14} className="text-red-400/60" />
          <span className="text-cream/40 text-xs font-medium">Blocked Users ({blockedUsers.length})</span>
        </div>
        <ChevronDown size={13} className={`text-cream/20 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="border-t border-red-500/[0.06] px-5 py-4 space-y-2">
          {blockedUsers.map(u => (
            <div key={u.reader_id} className="flex items-center justify-between py-2 border-b border-cream/[0.03] last:border-0">
              <div className="flex items-center gap-2">
                <User size={11} className="text-cream/20" />
                <span className="text-cream/30 text-xs font-mono">{u.reader_id.slice(0, 12)}…</span>
                <span className="text-cream/15 text-[10px]">blocked {new Date(u.blocked_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
              </div>
              <button
                onClick={() => onUnblock(u.reader_id)}
                disabled={unblockLoading === u.reader_id}
                data-testid={`unblock-btn-${u.reader_id}`}
                className="text-[10px] text-cream/25 hover:text-cream/60 border border-cream/[0.06] hover:border-cream/[0.12] px-2 py-1 rounded-lg transition-colors disabled:opacity-50"
              >
                {unblockLoading === u.reader_id ? <Loader2 size={10} className="animate-spin" /> : 'Unblock'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Main Component ── */
const AdminComments = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [comments, setComments] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, hidden: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [unblockLoading, setUnblockLoading] = useState(null);
  const [toast, setToast] = useState(null);

  const mutHeaders = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrf(),
    credentials: 'include',
  };

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/cms/comments/stats`, { credentials: 'include' });
      if (r.ok) setStats(await r.json());
    } catch {}
  }, []);

  const fetchComments = useCallback(async (status) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/cms/comments?status=${status}&limit=100`, { credentials: 'include' });
      if (r.ok) { const d = await r.json(); setComments(d.comments || []); }
    } catch {}
    setLoading(false);
  }, []);

  const fetchBlocked = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/cms/blocked-users`, { credentials: 'include' });
      if (r.ok) { const d = await r.json(); setBlockedUsers(d.blocked_users || []); }
    } catch {}
  }, []);

  useEffect(() => {
    fetchStats();
    fetchBlocked();
  }, [fetchStats, fetchBlocked]);

  useEffect(() => {
    fetchComments(activeTab);
  }, [activeTab, fetchComments]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    const r = await fetch(`${API_URL}/api/cms/comments/${id}/approve`, { method: 'PUT', headers: mutHeaders, credentials: 'include' });
    if (r.ok) { showToast('Comment approved'); fetchComments(activeTab); fetchStats(); }
    else showToast('Failed', 'err');
    setActionLoading(null);
  };

  const handleHide = async (id) => {
    setActionLoading(id);
    const r = await fetch(`${API_URL}/api/cms/comments/${id}/hide`, { method: 'PUT', headers: mutHeaders, credentials: 'include' });
    if (r.ok) { showToast('Comment hidden'); fetchComments(activeTab); fetchStats(); }
    else showToast('Failed', 'err');
    setActionLoading(null);
  };

  const handleDelete = async (id) => {
    setActionLoading(id);
    const r = await fetch(`${API_URL}/api/cms/comments/${id}`, { method: 'DELETE', headers: mutHeaders, credentials: 'include' });
    if (r.ok) { showToast('Comment deleted'); fetchComments(activeTab); fetchStats(); }
    else showToast('Failed', 'err');
    setActionLoading(null);
  };

  const handleBlock = async (readerId, readerName) => {
    const r = await fetch(`${API_URL}/api/cms/comments/block-user?reader_id=${encodeURIComponent(readerId)}`, { method: 'POST', headers: mutHeaders, credentials: 'include' });
    if (r.ok) { showToast(`${readerName} blocked`); fetchComments(activeTab); fetchStats(); fetchBlocked(); }
    else showToast('Failed', 'err');
  };

  const handleUnblock = async (readerId) => {
    setUnblockLoading(readerId);
    const r = await fetch(`${API_URL}/api/cms/comments/block-user?reader_id=${encodeURIComponent(readerId)}`, { method: 'DELETE', headers: mutHeaders, credentials: 'include' });
    if (r.ok) { showToast('User unblocked'); fetchBlocked(); }
    else showToast('Failed', 'err');
    setUnblockLoading(null);
  };

  return (
    <div className="max-w-3xl" data-testid="admin-comments-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-cream text-xl font-serif font-light">Comment Moderation</h1>
          <p className="text-cream/25 text-xs mt-0.5">Review and approve reader comments before they appear publicly</p>
        </div>
        <button onClick={() => { fetchComments(activeTab); fetchStats(); }} className="w-8 h-8 flex items-center justify-center rounded-lg text-cream/20 hover:text-cream/60 hover:bg-cream/[0.05] transition-colors" data-testid="refresh-comments-btn">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mb-4 px-4 py-2.5 rounded-xl text-xs flex items-center justify-between ${toast.type === 'err' ? 'bg-red-500/10 border border-red-500/20 text-red-400' : 'bg-teal-500/10 border border-teal-500/20 text-teal-300'}`} data-testid="comments-toast">
          {toast.msg}
          <button onClick={() => setToast(null)}><X size={10} /></button>
        </div>
      )}

      {/* Status Tabs */}
      <div className="flex gap-2 mb-5">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`comments-tab-${tab.id}`}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs transition-all border ${
              activeTab === tab.id
                ? `${tab.bg} ${tab.color}`
                : 'border-transparent text-cream/30 hover:text-cream/50'
            }`}
          >
            {tab.label}
            {stats[tab.id] > 0 && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${tab.id === 'pending' ? 'bg-amber-500/20 text-amber-400' : 'bg-cream/[0.06] text-cream/30'}`}>
                {stats[tab.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Comment List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={20} className="animate-spin text-cream/20" />
        </div>
      ) : comments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16" data-testid="no-comments-empty">
          <MessageCircle size={28} className="text-cream/10 mb-3" />
          <p className="text-cream/20 text-sm">No {activeTab} comments</p>
          {activeTab === 'pending' && (
            <p className="text-cream/10 text-xs mt-1">All caught up — nothing awaiting review</p>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="comments-list">
          {comments.map(c => (
            <CommentCard
              key={c.id}
              comment={c}
              onApprove={handleApprove}
              onHide={handleHide}
              onDelete={handleDelete}
              onBlock={handleBlock}
              actionLoading={actionLoading}
            />
          ))}
        </div>
      )}

      {/* Blocked Users */}
      <BlockedUsersPanel blockedUsers={blockedUsers} onUnblock={handleUnblock} unblockLoading={unblockLoading} />
    </div>
  );
};

export default AdminComments;
